import { useState, useCallback, useEffect, useMemo } from "react";

const STORAGE_KEYS = {
	projects: "tracking_recent_projects",
	tasks: "tracking_recent_tasks",
};

const MAX_RECENT_ITEMS = 5;

const useRecentItems = (projects, tasks) => {
	const [recentProjectIds, setRecentProjectIds] = useState([]);
	const [recentTaskIds, setRecentTaskIds] = useState([]);

	useEffect(() => {
		try {
			const storedProjects = localStorage.getItem(STORAGE_KEYS.projects);
			const storedTasks = localStorage.getItem(STORAGE_KEYS.tasks);

			if (storedProjects) {
				setRecentProjectIds(JSON.parse(storedProjects));
			}
			if (storedTasks) {
				setRecentTaskIds(JSON.parse(storedTasks));
			}
		} catch (e) {
			console.error("Error loading recent items from localStorage", e);
		}
	}, []);

	const addRecentProject = useCallback((projectId) => {
		if (!projectId) return;

		setRecentProjectIds((prev) => {
			const filtered = prev.filter((id) => id !== projectId);
			const updated = [projectId, ...filtered].slice(0, MAX_RECENT_ITEMS);
			localStorage.setItem(STORAGE_KEYS.projects, JSON.stringify(updated));
			return updated;
		});
	}, []);

	const addRecentTask = useCallback((taskId) => {
		if (!taskId) return;

		setRecentTaskIds((prev) => {
			const filtered = prev.filter((id) => id !== taskId);
			const updated = [taskId, ...filtered].slice(0, MAX_RECENT_ITEMS);
			localStorage.setItem(STORAGE_KEYS.tasks, JSON.stringify(updated));
			return updated;
		});
	}, []);

	const sortedProjects = useMemo(() => {
		if (!projects) return projects;
		if (!recentProjectIds.length) {
			return [...projects].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
		}

		const recentItems = [];
		const otherItems = [];

		projects.forEach((project) => {
			if (recentProjectIds.includes(project.id)) {
				recentItems.push({ ...project, __isRecent: true });
			} else {
				otherItems.push(project);
			}
		});

		recentItems.sort((a, b) => recentProjectIds.indexOf(a.id) - recentProjectIds.indexOf(b.id));
		otherItems.sort((a, b) => (a.name || "").localeCompare(b.name || ""));

		if (recentItems.length > 0 && otherItems.length > 0) {
			return [...recentItems, { __isSeparator: true, id: "__separator__" }, ...otherItems];
		}
		return [...recentItems, ...otherItems];
	}, [projects, recentProjectIds]);

	const sortedTasks = useMemo(() => {
		if (!tasks) return tasks;
		if (!recentTaskIds.length) {
			return [...tasks].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
		}

		const recentItems = [];
		const otherItems = [];

		tasks.forEach((task) => {
			if (recentTaskIds.includes(task.id)) {
				recentItems.push({ ...task, __isRecent: true });
			} else {
				otherItems.push(task);
			}
		});

		recentItems.sort((a, b) => recentTaskIds.indexOf(a.id) - recentTaskIds.indexOf(b.id));
		otherItems.sort((a, b) => (a.name || "").localeCompare(b.name || ""));

		if (recentItems.length > 0 && otherItems.length > 0) {
			return [...recentItems, { __isSeparator: true, id: "__separator__" }, ...otherItems];
		}
		return [...recentItems, ...otherItems];
	}, [tasks, recentTaskIds]);

	return {
		addRecentProject,
		addRecentTask,
		sortedProjects,
		sortedTasks,
	};
};

export default useRecentItems;
